import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';

function Breadcrumbs() {
  const location = useLocation();

  // Hide breadcrumbs on dashboard and non-admin paths
  if (!location.pathname.startsWith('/admin/') || location.pathname === '/admin/dashboard') {
    return null;
  }

  // Filter out empty strings and 'admin'
  const pathnames = location.pathname
    .split('/')
    .filter((x) => x !== '' && x !== 'admin');

  // Create a mapping of path segments to readable names and icons
  const pathMap = {
    'dashboard': {
      name: 'Dashboard',
      icon: 'fluent:home-24-regular'
    },
    'roles-and-access': {
      name: 'Roles & Access',
      icon: 'fluent:shield-lock-24-regular'
    },
    'courses': {
      name: 'All Courses',
      icon: 'fluent:book-24-regular'
    },
    'edit-create': {
      name: 'Edit Course',
      icon: 'fluent:edit-24-regular'
    },
    'livesessions': {
      name: 'LiveSessions',
      icon: 'fluent:video-person-24-regular'
    },
    'livesession-dashboard': {
      name: 'LiveSession Dashboard',
      icon: 'fluent:video-person-24-regular'
    },
    'trainees': {
      name: 'Trainees',
      icon: 'fluent:people-24-regular'
    },
    'assessment': {
      name: 'Assessment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'LiveSessionDashboard': {
      name: 'LiveSession Dashboard',
      icon: 'fluent:video-person-24-regular'
    },
    'LiveSessionAssessmentDetails': {
      name: 'Assessment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'assingment': {
      name: 'Assignment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'LiveSessionAssignmentDetails': {
      name: 'Assignment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'analytics': {
      name: 'Course Analytics',
      icon: 'fluent:data-trending-24-regular'
    },
    'module': {
      name: 'Course Modules',
      icon: 'fluent:book-information-24-regular'
    },
    'content': {
      name: 'Content',
      icon: 'fluent:document-24-regular'
    },
    'video': {
      name: 'Edit Video',
      icon: 'fluent:video-24-regular'
    },
    'quiz': {
      name: 'Edit Assessment',
      icon: 'fluent:clipboard-task-list-ltr-24-regular'
    },
    'survey': {
      name: 'Edit Survey',
      icon: 'fluent:form-24-regular'
    },
    'document': {
      name: 'Edit Document',
      icon: 'fluent:document-24-regular'
    }
  };

  const getPathInfo = (path) => {
    // Handle encoded IDs
    if (path.includes('%')) {
      const pathType = pathnames[pathnames.indexOf(path) - 1];
      switch (pathType) {
        case 'LiveSessionDashboard':
          return {
            name: 'LiveSession Dashboard',
            icon: 'fluent:video-person-24-regular'
          };
        case 'assessment':
          return {
            name: 'Assessment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'LiveSessionAssessmentDetails':
          return {
            name: 'Assessment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'assingment':
          return {
            name: 'Assignment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'LiveSessionAssignmentDetails':
          return {
            name: 'Assignment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'video':
          return {
            name: 'Edit Video',
            icon: 'fluent:video-24-regular'
          };
        case 'quiz':
          return {
            name: 'Edit Assessment',
            icon: 'fluent:clipboard-task-list-ltr-24-regular'
          };
        case 'survey':
          return {
            name: 'Edit Survey',
            icon: 'fluent:form-24-regular'
          };
        case 'document':
          return {
            name: 'Edit Document',
            icon: 'fluent:document-24-regular'
          };
        case 'content':
          return {
            name: 'Content',
            icon: 'fluent:document-24-regular'
          };
        default:
          return {
            name: 'Module Details',
            icon: 'fluent:book-information-24-regular'
          };
      }
    }

    return pathMap[path] || {
      name: path.charAt(0).toUpperCase() + path.slice(1),
      icon: 'fluent:document-24-regular'
    };
  };

  // Get the current page title
  const getPageTitle = () => {
    if (pathnames.length === 0) return 'Dashboard';

    if (pathnames.includes('roles-and-access')) {
      return 'Roles & Access';
    }

    if (pathnames.includes('traineesAnalytics')) {
      return 'Trainee Analytics';
    }

    if (pathnames.includes('CourseAnalyticsAssessmentDetails')) {
      return 'Assessment Analytics';
    }

    if (pathnames.includes('CourseAnalyticsSurveyDetails')) {
      return 'Survey Analytics';
    }

    if (pathnames.includes('edit-create')) {
      return 'Edit Course';
    }

    if (pathnames.includes('livesessions') && pathnames.length === 1) {
      return 'LiveSessions';
    }

    if (pathnames.includes('livesession-dashboard') && pathnames.length === 3) {
      return 'LiveSession Dashboard';
    }

    if (pathnames.includes('LiveSessionAssignmentDetails')) {
      return 'Assignment';
    }

    if (pathnames.includes('LiveSessionAssessmentDetails')) {
      return 'Assessment';
    }

    if (pathnames.includes('livesession-dashboard') && pathnames.includes('trainees')) {
      return 'Trainees';
    }

    const lastSegment = pathnames[pathnames.length - 2]; // Get the type (video, quiz, etc.)
    const pathInfo = getPathInfo(pathnames[pathnames.length - 1]);

    switch (lastSegment) {
      case 'analytics':
        return 'Course Analytics';
      case 'video':
        return 'Edit Video';
      case 'quiz':
        return 'Edit Assessment';
      case 'survey':
        return 'Edit Survey';
      case 'document':
        return 'Edit Document';
      case 'content':
        return 'Content';
      case 'module':
        return 'Course Modules';
      default:
        return pathInfo.name;
    }
  };

  // Build the breadcrumb path
  const buildPath = (index) => {
    return `/admin/${pathnames.slice(0, index + 1).join('/')}`;
  };

  // Custom breadcrumb items for specific pages
  const getBreadcrumbItems = () => {
    // Add new condition for roles and access
    if (pathnames.includes('roles-and-access')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Roles & Access',
          icon: 'fluent:shield-lock-24-regular',
          path: location.pathname
        }
      ];
    }

    // Handle trainee analytics path
    if (pathnames.includes('traineesAnalytics')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Trainees',
          icon: 'fluent:people-24-regular',
          path: '/admin/trainees'
        },
        {
          name: 'Trainee Analytics',
          icon: 'fluent:data-trending-24-regular',
          path: location.pathname
        }
      ];
    }

    // Handle LiveSessionDashboard assessment path
    if (pathnames.includes('LiveSessionAssessmentDetails')) {
      const livesessionId = pathnames[pathnames.indexOf('LiveSessionDashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'LiveSession Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: `/admin/livesessions/livesession-dashboard/${livesessionId}`
        },
        {
          name: 'Assessment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    // Handle LiveSessionDashboard assignment path
    if (pathnames.includes('LiveSessionAssignmentDetails')) {
      const livesessionId = pathnames[pathnames.indexOf('LiveSessionDashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'LiveSession Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: `/admin/livesessions/livesession-dashboard/${livesessionId}`
        },
        {
          name: 'Assignment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    if (pathnames.includes('analytics')) {
      const hasAssessmentDetails = pathnames.includes('CourseAnalyticsAssessmentDetails');
      const hasSurveyDetails = pathnames.includes('CourseAnalyticsSurveyDetails');

      if (hasAssessmentDetails) {
        return [
          {
            name: 'Dashboard',
            icon: 'fluent:home-24-regular',
            path: '/admin/dashboard'
          },
          {
            name: 'All Courses',
            icon: 'fluent:book-24-regular',
            path: '/admin/courses'
          },
          {
            name: 'Course Analytics',
            icon: 'fluent:data-trending-24-regular',
            path: `/admin/courses/analytics/${pathnames[pathnames.indexOf('analytics') + 1]}`
          },
          {
            name: 'Assessment Analytics',
            icon: 'fluent:clipboard-task-list-24-regular',
            path: location.pathname
          }
        ];
      }

      if (hasSurveyDetails) {
        return [
          {
            name: 'Dashboard',
            icon: 'fluent:home-24-regular',
            path: '/admin/dashboard'
          },
          {
            name: 'All Courses',
            icon: 'fluent:book-24-regular',
            path: '/admin/courses'
          },
          {
            name: 'Course Analytics',
            icon: 'fluent:data-trending-24-regular',
            path: `/admin/courses/analytics/${pathnames[pathnames.indexOf('analytics') + 1]}`
          },
          {
            name: 'Survey Analytics',
            icon: 'fluent:form-24-regular',
            path: location.pathname
          }
        ];
      }

      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'All Courses',
          icon: 'fluent:book-24-regular',
          path: '/admin/courses'
        },
        {
          name: 'Course Analytics',
          icon: 'fluent:data-trending-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for livesession trainees
    if (pathnames.includes('livesession-dashboard') && pathnames.includes('trainees')) {
      const livesessionId = pathnames[pathnames.indexOf('livesession-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'LiveSessions',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/livesessions'
        },
        {
          name: 'LiveSession Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: `/admin/livesessions/livesession-dashboard/${livesessionId}`
        },
        {
          name: 'Trainees',
          icon: 'fluent:people-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for livesession assessment details
    if (pathnames.includes('LiveSessionAssessmentDetails')) {
      const livesessionId = pathnames[pathnames.indexOf('livesession-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'LiveSessions',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/livesessions'
        },
        {
          name: 'LiveSession Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: `/admin/livesessions/livesession-dashboard/${livesessionId}`
        },
        {
          name: 'Assessment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for livesession assignment details
    if (pathnames.includes('LiveSessionAssignmentDetails')) {
      const livesessionId = pathnames[pathnames.indexOf('livesession-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'LiveSessions',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/livesessions'
        },
        {
          name: 'LiveSession Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: `/admin/livesessions/livesession-dashboard/${livesessionId}`
        },
        {
          name: 'Assignment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for livesession dashboard
    if (pathnames.includes('livesession-dashboard')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'LiveSessions',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/livesessions'
        },
        {
          name: 'LiveSession Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for livesessions main page
    if (pathnames.includes('livesessions') && pathnames.length === 1) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'LiveSessions',
          icon: 'fluent:video-person-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for edit-create course
    if (pathnames.includes('edit-create')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'All Courses',
          icon: 'fluent:book-24-regular',
          path: '/admin/courses'
        },
        {
          name: 'Edit Course',
          icon: 'fluent:edit-24-regular',
          path: location.pathname
        }
      ];
    }

    if (pathnames.includes('module') && pathnames.includes('content')) {
      const moduleIndex = pathnames.indexOf('module');
      const contentIndex = pathnames.indexOf('content');
      const contentType = pathnames[contentIndex + 2];
      const typeNames = {
        'video': 'Edit Video',
        'quiz': 'Edit Assessment',
        'survey': 'Edit Survey',
        'document': 'Edit Document'
      };
      const typeIcons = {
        'video': 'fluent:video-24-regular',
        'quiz': 'fluent:clipboard-task-list-ltr-24-regular',
        'survey': 'fluent:form-24-regular',
        'document': 'fluent:document-24-regular'
      };

      return [
        { name: 'Dashboard',      icon: 'fluent:home-24-regular',             path: '/admin/dashboard' },
        { name: 'All Courses',    icon: 'fluent:book-24-regular',             path: '/admin/courses' },
        { name: 'Course Modules', icon: 'fluent:book-information-24-regular', path: `/admin/courses/module/${pathnames[moduleIndex + 1]}` },
        { name: 'Content',        icon: 'fluent:document-24-regular',         path: `/admin/courses/module/${pathnames[moduleIndex + 1]}` },
        {
          name: typeNames[contentType] || 'Content Details',
          icon: typeIcons[contentType] || 'fluent:document-24-regular',
          path: location.pathname
        }
      ];
    } else if (pathnames.includes('module')) {
      const moduleIndex = pathnames.indexOf('module');
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'All Courses',
          icon: 'fluent:book-24-regular',
          path: '/admin/courses'
        },
        {
          name: 'Course Modules',
          icon: 'fluent:book-information-24-regular',
          path: location.pathname
        }
      ];
    }

    return null;
  };

  const customBreadcrumbs = getBreadcrumbItems();

  const renderItems = (items) => {
    const visibleItems = items.length <= 3 ? items : [items[0], null, items[items.length - 1]];

    return visibleItems.map((item, index) => (
      <React.Fragment key={index}>
        {index > 0 && <li className="text-font-secondary"><Icon icon="fluent:chevron-right-24-regular" width="16" /></li>}
        <li>
          {item === null ? (
            <Link
              to={items[items.length - 2].path}
              className="text-font-secondary hover:text-primary font-bold transition-colors"
            >...</Link>
          ) : index === visibleItems.length - 1 ? (
            <span className="text-primary font-semibold flex items-center">
              <Icon icon={item.icon} className="mr-1" width="16" />{item.name}
            </span>
          ) : (
            <Link to={item.path} className="text-font-secondary hover:text-primary flex items-center transition-colors duration-200">
              <Icon icon={item.icon} className="mr-1" width="16" />{item.name}
            </Link>
          )}
        </li>
      </React.Fragment>
    ));
  };

  const allItems = customBreadcrumbs ?? [
    { name: 'Dashboard', icon: 'fluent:home-24-regular', path: '/admin/dashboard' },
    ...pathnames.map((value, index) => ({ ...getPathInfo(value), path: buildPath(index) }))
  ];

  // Always ensure last item reflects current page
  if (allItems.length > 0) {
    allItems[allItems.length - 1].path = location.pathname;
  }

  return (
    <nav aria-label="breadcrumb" className="py-2 mb-3">

      {/* Mobile: back arrow + current tab only */}
      <div className="flex md:hidden items-center gap-2 text-sm mb-2">
        {allItems.length > 1 && (
          <Link to={allItems[allItems.length - 2].path} className="text-font-secondary bg-surface rounded-lg p-2 hover:text-primary shrink-0">
            <Icon icon="solar:alt-arrow-left-linear" width="18" />
          </Link>
        )}
        <span className="text-primary font-semibold truncate">{allItems[allItems.length - 1]?.name}</span>
      </div>

      {/* Desktop: full breadcrumb */}
      <ol className="hidden md:flex items-center space-x-2 text-sm">
        {renderItems(allItems)}
      </ol>

    </nav>
  );
}

export default Breadcrumbs;